// src/services/sdkLogConfig.js
// Quiets the matrix-js-sdk + matrix-rust-crypto (WASM) console logging in production.
//
// Why this exists (NED-4003 "Excessive Console Errors and Rapid Log Generation During
// Login"): the matrix-js-sdk loglevel logger defaults to DEBUG and the app never raised
// it, so every login dumped the rust crypto-store schema migrations (INFO), the
// key-backup-downloader status (INFO), dehydration steps (INFO) and a per-event
// decryption WARNING for every historical message a fresh device can't decrypt — tens of
// thousands of lines / multiple MB in the console once key backup isn't available.
//
// The SDK singleton logger is ALSO the `JsLogger` that matrix-js-sdk hands to the
// rust-crypto WASM (StoreHandle.open / OlmMachine.initFromStore), so a single setLevel()
// governs BOTH the JS-side SDK logs and the WASM `tracing` output — there is no separate
// wasm-to-console subscriber to silence in matrix-sdk-crypto-wasm 17.x.
//
// Level policy: production -> WARN, development -> DEBUG, overridable via
// localStorage['TI-M_LOG_LEVEL'] (the same key the app's own logger reads, so a single
// override dials both). This is intentionally NOT identical to src/utils/logger.js, which
// defaults to ERROR in production: the SDK is kept one level more verbose (WARN) so the
// genuinely diagnostic warnings survive — pending /keys/query, "Failed to get TURN URIs",
// push-rule defaults — while the routine INFO bookkeeping (crypto-store migrations,
// key-backup-downloader status, dehydration) is dropped.
//
// Decryption-warning throttle (NED-4003 restart): two WARN/ERROR patterns multiply ONCE
// PER undecryptable event/room — "Failed to decrypt a room event" (emitted by the
// rust-crypto WASM through this same singleton logger) and "Error decrypting event (...)"
// (matrix-js-sdk models/event.js). For a fresh device before key backup restore that is
// 2 lines per historical message — hundreds for a real account. Raising the level to ERROR
// would kill those AND every other genuine WARN, which the operator wants kept. Instead we
// wrap the loglevel `methodFactory` (the single seam every JS + WASM log line passes
// through) and collapse just those two patterns into ONE throttled summary WARN per burst,
// so the diagnostic signal ("decryption is failing") survives at WARN in production without
// the per-event volume. Every other line is untouched.
//
// Escape hatch: localStorage['TI-M_SDK_LOG_UNFILTERED'] === '1' (read once at install time)
// restores the full per-event output for debugging. Note the throttle layers UNDER the level
// policy: at TI-M_LOG_LEVEL=error the warn channel is disabled by loglevel itself, so the
// summary is also silenced (error-only ⇒ no warnings at all — intentional, not a regression).
//
// This file lives in src/services/** — the encapsulation layer that is permitted to
// import matrix-js-sdk directly (see eslint no-restricted-imports config).

import { logger as sdkLogger } from 'matrix-js-sdk/lib/logger';

// App-level names (src/utils/logger.js) that map 1:1 onto loglevel's level strings.
const VALID_LEVELS = ['error', 'warn', 'info', 'debug'];

// Patterns whose log lines multiply once per event/room. Verified against matrix-js-sdk
// 41.0.0 / @matrix-org/matrix-sdk-crypto-wasm 17.1.0. "Failed to decrypt a room event" is
// rust-WASM wording (not greppable in the JS lib); a reword fails OPEN (the per-event lines
// reappear, nothing is hidden) — re-check on SDK upgrade.
const SUPPRESS_PATTERNS = [
  'Failed to decrypt a room event',
  'Error decrypting event',
];

// Fixed-window throttle: the first suppressed line opens this window; later lines in the
// same window do NOT reset it (a continuous backfill still surfaces ~1 summary / window
// instead of starving until the stream pauses).
const SUMMARY_WINDOW_MS = 2000;

let installed = false;
let suppressedCount = 0;
let summaryTimer = null;

const resolveLevel = () => {
  try {
    const override = localStorage.getItem('TI-M_LOG_LEVEL');
    if (override && VALID_LEVELS.includes(override.toLowerCase())) {
      return override.toLowerCase();
    }
  } catch {
    // localStorage may be unavailable (private mode / blocked) — fall back to env default.
  }
  return process.env.NODE_ENV === 'production' ? 'warn' : 'debug';
};

/**
 * Pure predicate: true iff the first log arg (the message — rust-crypto sends one
 * multi-line string, models/event.js sends a string, and loglevel's prefix unshift happens
 * INSIDE the wrapped emit, after this inspection) contains a multiplying-decryption pattern.
 */
export function isMultiplyingDecryptionLog(args) {
  let first;
  try {
    // This coercion runs on EVERY warn/error for the app's lifetime, including SDK call
    // sites that log a bare object (e.g. logger.error(err)). A pathological first arg with a
    // throwing toString/Symbol.toPrimitive must never turn a quiet log into a thrown error —
    // fail OPEN so the line is NOT suppressed and reaches the console untouched, exactly as
    // the stock SDK's console.* would (which never coerces it).
    first = String(args?.[0] ?? '');
  } catch {
    return false;
  }
  return SUPPRESS_PATTERNS.some((pattern) => first.includes(pattern));
}

// Open the fixed-window timer (if not already open) that flushes one summary WARN.
function scheduleSummary() {
  if (summaryTimer) return;
  summaryTimer = setTimeout(() => {
    const n = suppressedCount;
    suppressedCount = 0;
    summaryTimer = null;
    try {
      // Routed through the normal logger so it obeys the level policy and the filter (the
      // summary string matches no SUPPRESS_PATTERNS, so it passes through to the console).
      sdkLogger.warn(
        `[TI-M] suppressed ${n} decryption-failure log line(s) (events pending key backup restore)`,
      );
    } catch {
      // A failing summary must never surface as an unhandled error.
    }
  }, SUMMARY_WINDOW_MS);
}

/**
 * Pure wrapper-builder around a loglevel emit function. For warn/error it drops the two
 * multiplying-decryption patterns (counting them toward the next summary) and passes
 * everything else through untouched; other levels are never filtered. Exported so the
 * suppression logic is unit-testable without a live loglevel logger.
 */
export function wrapEmit(methodName, emit) {
  return function wrappedEmit(...args) {
    if ((methodName === 'warn' || methodName === 'error') && isMultiplyingDecryptionLog(args)) {
      suppressedCount++;
      scheduleSummary();
      return undefined;
    }
    return emit.apply(this, args);
  };
}

/**
 * Wrap the SDK logger's `methodFactory` so every level method it builds (and every child
 * logger + the rust-crypto WASM JsLogger that inherit the factory) routes through wrapEmit.
 * Idempotent; installed once before the first createClient()/initRustCrypto().
 */
function installSdkLogFilter() {
  if (installed) return;
  try {
    // Read once at install time — no live toggling. Early-return leaves installed=false
    // (harmless: the filter simply stays off for this load).
    if (localStorage.getItem('TI-M_SDK_LOG_UNFILTERED') === '1') return;
  } catch {
    // localStorage unavailable — proceed with filtering on (the safe default).
  }
  // The real loglevel logger always exposes methodFactory; the bare jest stub does not.
  // Guard so install no-ops against that stub (and never calls undefined(...)) if the seam
  // ever changes.
  if (typeof sdkLogger.methodFactory !== 'function') return;
  const originalFactory = sdkLogger.methodFactory;
  sdkLogger.methodFactory = (methodName, logLevel, loggerName) =>
    wrapEmit(methodName, originalFactory(methodName, logLevel, loggerName));
  installed = true;
}

/**
 * Install the decryption-warning throttle and apply the resolved log level to the
 * matrix-js-sdk singleton logger.
 *
 * Must run BEFORE the first initRustCrypto() so the WASM picks up the already-leveled,
 * already-filtered JsLogger. Safe to call repeatedly: the filter install is idempotent and
 * loglevel.setLevel rebuilds the level methods through the (now wrapped) factory each time.
 * Callers may invoke it from every createClient() path defensively.
 *
 * Note: child loggers created later via logger.getChild() copy the parent's methodFactory at
 * creation time (SDK logger.js), so they inherit the wrapper installed here.
 */
export function configureSdkLogging() {
  try {
    // Install before setLevel: loglevel's setLevel (replaceLoggingMethods) rebuilds the
    // level methods using whatever methodFactory is current, so the wrapper must be in place
    // first. A filter-install failure must never block the level policy below.
    installSdkLogFilter();
  } catch {
    // Never let the filter install break client startup.
  }
  try {
    // persist=false: don't write loglevel's own localStorage key; we re-apply on each load.
    sdkLogger.setLevel(resolveLevel(), false);
  } catch {
    // Never let logging configuration break client startup.
  }
}

// Dev/test-only hooks (mirrors serverConfigService.__testing__); tree-shaken from
// production bundles. SUMMARY_WINDOW_MS is exposed so tests derive their fake-timer advances
// from the single source of truth rather than re-hardcoding the window.
export const __testing__ = process.env.NODE_ENV !== 'production' ? {
  SUMMARY_WINDOW_MS,
  resetFilter() {
    installed = false;
    suppressedCount = 0;
    clearTimeout(summaryTimer);
    summaryTimer = null;
  },
} : undefined;
